import React from "react"
import { convertMilitaryTimeTo12HourFormat } from "../../../utils/generalUtils"

/**
 * Calculates and prepares all data needed for the Nurse Workload graph
 * @param {Array} data - The historical data from the API
 * @param {Object} options - Additional options like height, chartHeight, type
 * @returns {Object} - All data needed for rendering the Nurse Workload in a BlankCard
 */
export const createNurseWorkload = (data, options = {}) => {
  const {
    height = 300,
    chartHeight = 225,
    type = "acuity"
  } = options

  // Define colors for the different workload categories using text colors
  const colors = ["#AEA2FF", "#6955EA", "#8535c4", "#352D66"] // green-400, purple-400, yellow-400, red-400
  
  // Get center operating hours from the provided settings or use defaults
  const openTime = options.openTime || "8:00"
  const closeTime = options.closeTime || "17:00"

  // Parse open and close hours
  const parseTime = timeString => {
    const [hours] = timeString.split(":").map(Number)
    return hours
  }

  const openHour = parseTime(openTime)
  const closeHour = parseTime(closeTime)

  // Dropdown component for switching between acuity and patient load views
  const WorkloadTypeDropdown = () => {
    const handleTypeChange = (e) => {
      const newType = e.target.value
      if (options.onTypeChange) {
        options.onTypeChange(newType)
      }
    }
    
    return (
      <div className="mt-4 flex justify-start pl-4">
        <div className="inline-block">
          <label className="text-sm text-gray-500 mr-2">View by:</label>
          <select 
            value={type}
            onChange={handleTypeChange}
            className="text-sm border-gray-300 rounded-md shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
          >
            <option value="acuity">Acuity Based</option>
            <option value="patients">Patient Based</option>
          </select>
        </div>
      </div>
    )
  }

  // Updated transformDataByHour function to properly format times
  const transformDataByHour = () => {
    // Create an array for each hour of operation
    const hours = []
    for (let hour = openHour; hour <= closeHour; hour++) {
      hours.push(hour)
    }

    // Initialize data structure for each hour
    const hourlyData = hours.map(hour => ({
      timeSlot: `${hour}:00`,
      available: 0,
      nearCapacity: 0,
      atCapacity: 0,
      overCapacity: 0,
      // Track unique dates for average calculation
      processedDates: new Set()
    }))

    // If there is no data, return the empty structure
    if (!data || data.length === 0) {
      return hourlyData.map(
        ({ timeSlot, available, nearCapacity, atCapacity, overCapacity }) => ({
          timeSlot: formatTimeForDisplay(timeSlot),
          available,
          nearCapacity,
          atCapacity,
          overCapacity
        })
      )
    }

    // Process each data point
    data.forEach(dayData => {
      // Skip if no valid nurse_workload data
      if (!dayData) return
      const date = dayData.date

      // Process hourly data based on the current type
      if (type === "acuity") {
        if (dayData?.hourly) {
          Object.entries(dayData.hourly).forEach(([minuteKey, hourData]) => {
            // Convert minute key to hour (e.g., "480" -> 8)
            const hourNum = Math.floor(parseInt(minuteKey) / 60)

            // Skip if the hour is outside operating hours
            if (hourNum < openHour || hourNum > closeHour) return

            // Find the index for this hour
            const hourIndex = hourNum - openHour

            // Skip if hour index is invalid
            if (hourIndex < 0 || hourIndex >= hourlyData.length) return

            // Get data for this hour
            const hourDataObj = hourlyData[hourIndex]

            // Track this date if not already processed for this hour
            if (!hourDataObj.processedDates.has(date)) {
              hourDataObj.processedDates.add(date)
            }

            // Add nurse counts to the appropriate category
            // Handle both direct values and MongoDB format with $numberInt
            hourDataObj.available += parseInt(
              hourData.availableNurses?.$numberInt || hourData.availableNurses || 0
            )

            hourDataObj.nearCapacity += parseInt(
              hourData.nearCapacityNurses?.$numberInt ||
              hourData.nearCapacityNurses ||
              0
            )

            hourDataObj.atCapacity += parseInt(
              hourData.atCapacityNurses?.$numberInt || hourData.atCapacityNurses || 0
            )

            hourDataObj.overCapacity += parseInt(
              hourData.overCapacityNurses?.$numberInt ||
              hourData.overCapacityNurses ||
              0
            )
          })
        }
      } else if (type === "patients") {
        if (dayData?.hourlyPatients) {
          Object.entries(dayData.hourlyPatients).forEach(([minuteKey, hourData]) => {
            // Convert minute key to hour (e.g., "480" -> 8)
            const hourNum = Math.floor(parseInt(minuteKey) / 60)

            // Skip if the hour is outside operating hours
            if (hourNum < openHour || hourNum > closeHour) return

            // Find the index for this hour
            const hourIndex = hourNum - openHour

            // Skip if hour index is invalid
            if (hourIndex < 0 || hourIndex >= hourlyData.length) return

            // Get data for this hour
            const hourDataObj = hourlyData[hourIndex]

            // Track this date if not already processed for this hour
            if (!hourDataObj.processedDates.has(date)) {
              hourDataObj.processedDates.add(date)
            }

            // Add nurse counts to the appropriate category
            // Handle both direct values and MongoDB format with $numberInt
            hourDataObj.available += parseInt(
              hourData.availableNurses?.$numberInt || hourData.availableNurses || 0
            )

            hourDataObj.nearCapacity += parseInt(
              hourData.nearCapacityNurses?.$numberInt ||
              hourData.nearCapacityNurses ||
              0
            )

            hourDataObj.atCapacity += parseInt(
              hourData.atCapacityNurses?.$numberInt || hourData.atCapacityNurses || 0
            )

            hourDataObj.overCapacity += parseInt(
              hourData.overCapacityNurses?.$numberInt ||
              hourData.overCapacityNurses ||
              0
            )
          })
        }
      }
    })

    // Calculate averages for the entire selected date range
    return hourlyData.map(hour => {
      const uniqueDaysCount = hour.processedDates.size || 1 // Avoid division by zero
      return {
        timeSlot: formatTimeForDisplay(hour.timeSlot),
        available: Math.round(hour.available / uniqueDaysCount),
        nearCapacity: Math.round(hour.nearCapacity / uniqueDaysCount),
        atCapacity: Math.round(hour.atCapacity / uniqueDaysCount),
        overCapacity: Math.round(hour.overCapacity / uniqueDaysCount)
      }
    })
  }

  // Define a helper function for properly formatting the time
  const formatTimeForDisplay = (timeString) => {
    // Parse the hour and ensure it has the correct format
    const [hour] = timeString.split(':');
    const hourNum = parseInt(hour);
    const ampm = hourNum >= 12 ? 'PM' : 'AM';
    const hour12 = hourNum % 12 || 12; // Convert to 12-hour format (0 becomes 12)

    return `${hour12} ${ampm}`;
  };

  const hourlyData = transformDataByHour()

  // Calculate statistics from the data
  const calculateStats = () => {
    // Calculate average nurses per hour
    const avgNursesPerHour = hourlyData.reduce((acc, hour) => {
      const hourTotal = hour.available + hour.nearCapacity + hour.atCapacity + hour.overCapacity
      return acc + hourTotal
    }, 0) / hourlyData.length || 1 // Default to 1 to avoid division by zero
    
    // Extract or calculate metrics from the data
    // 1. Average acuity per nurse (assuming this is passed in the data)
    const avgAcuityPerNurse = data.reduce((sum, day) => 
      sum + (day.averageAcuityPerNurse || 0), 0) / data.length || 3.6
    
    // 2. Average overtime per nurse
    const totalOvertimePerDay = data.reduce((sum, day) => 
      sum + (day.overtimePerDay || 0), 0) / data.length
    const overtimePerNurse = totalOvertimePerDay / avgNursesPerHour
    
    // 3. Average patients per nurse
    const avgPatientsPerNurse = data.reduce((sum, day) => 
      sum + (day.averagePatientsPerNurse || 0), 0) / data.length || 4.8
    
    return {
      averageAcuityPerNurse: avgAcuityPerNurse.toFixed(1), // From data or default to 3.6
      overtimePerNurse: overtimePerNurse.toFixed(1) + 'h', // Calculate from overtime and nurse count
      patientsPerNurse: avgPatientsPerNurse.toFixed(1),    // From data or default to 4.8
      peakHour: hourlyData.reduce((max, hour) => {
        const overCapacityTotal = hour.overCapacity;
        const atCapacityTotal = hour.atCapacity;
        const nearCapacityTotal = hour.nearCapacity;
        const availableTotal = hour.available;

        if (overCapacityTotal > max.overCapacity) {
          return { time: hour.timeSlot, overCapacity: overCapacityTotal, atCapacity: atCapacityTotal, nearCapacity: nearCapacityTotal, available: availableTotal };
        } else if (overCapacityTotal === max.overCapacity) {
          if (atCapacityTotal > max.atCapacity) {
            return { time: hour.timeSlot, overCapacity: overCapacityTotal, atCapacity: atCapacityTotal, nearCapacity: nearCapacityTotal, available: availableTotal };
          } else if (atCapacityTotal === max.atCapacity) {
            if (nearCapacityTotal > max.nearCapacity) {
              return { time: hour.timeSlot, overCapacity: overCapacityTotal, atCapacity: atCapacityTotal, nearCapacity: nearCapacityTotal, available: availableTotal };
            } else if (nearCapacityTotal === max.nearCapacity) {
              if (availableTotal > max.available) {
                return { time: hour.timeSlot, overCapacity: overCapacityTotal, atCapacity: atCapacityTotal, nearCapacity: nearCapacityTotal, available: availableTotal };
              }
            }
          }
        }
        return max;
      }, { time: '', overCapacity: 0, atCapacity: 0, nearCapacity: 0, available: 0 }).time
    }
  }

  const getStatus = () => {
    // Calculate the percentage of nurses who are over capacity
    const overCapNurses = hourlyData.reduce((sum, hour) => sum + hour.overCapacity, 0)
    const underCapNurses = hourlyData.reduce((sum, hour) => sum + hour.available, 0)
    const totalNurses = hourlyData.reduce((sum, hour) => {
      return sum + hour.available + hour.nearCapacity + hour.atCapacity + hour.overCapacity
    }, 0)
    
    const overCapacityRate = totalNurses > 0 ? (overCapNurses / totalNurses) * 100 : 0
    const underCapacityRate = totalNurses > 0 ? (underCapNurses / totalNurses) * 100 : 0
    
    // Determine status based on capacity rates
    if (overCapacityRate > 15) {
      return { 
        status: "critical",
        badge: "Nurses Overutilized"
      }
    }
    if (underCapacityRate > 30) {
      return { 
        status: "warning",
        badge: "Nurses Underutilized"
      }
    }
    return { 
      status: "good",
      badge: "Properly Utilized"
    }
  }
  
  const statusInfo = getStatus()

  // Analyze the hourly data to identify periods of under/over utilization
  const analyzeUtilization = () => {
    let underutilizedHours = 0;
    let overutilizedHours = 0;
    
    // Track hours with utilization issues
    const underutilizedTimeSlots = [];
    const overutilizedTimeSlots = [];
    
    hourlyData.forEach(hour => {
      // Helper function to check if a time slot is after 10 AM
      const isAfter10AM = (timeSlot) => {
        // Split the time slot like "8 AM - 9 AM" or "10 AM - 11 AM"
        const startTime = timeSlot.split(' - ')[0]; // Get "8 AM" or "10 AM"
        const hour = parseInt(startTime.split(' ')[0]); // Get 8 or 10
        const period = startTime.split(' ')[1]; // Get "AM" or "PM"
        
        // Return true if it's 10 AM or later (any PM time will also be after 10 AM)
        return period === "PM" || (period === "AM" && hour >= 10);
      };

      // Check for underutilization (≥5 available nurses AND after 10 AM)
      if (hour.available >= 5 && isAfter10AM(hour.timeSlot)) {
        underutilizedHours++;
        underutilizedTimeSlots.push(hour.timeSlot);
      }
      
      // Check for overutilization (≥1 overCapacity nurses AND 0 available nurses)
      if (hour.overCapacity >= 1 && hour.available === 0) {
        overutilizedHours++;
        overutilizedTimeSlots.push(hour.timeSlot);
      }
    });
    
    // Format time ranges for display
    const formatTimeRanges = (timeSlots) => {
      if (timeSlots.length === 0) return "";

      // Sort and group time slots (existing code remains the same)
      const sortedTimeSlots = [...timeSlots].sort((a, b) => {
        const [timeA, ampmA] = a.split(' ');
        const hourA = parseInt(timeA);
        const hour24A = hourA + (ampmA === 'PM' && hourA !== 12 ? 12 : 0) - (ampmA === 'AM' && hourA === 12 ? 12 : 0);
        
        const [timeB, ampmB] = b.split(' ');
        const hourB = parseInt(timeB);
        const hour24B = hourB + (ampmB === 'PM' && hourB !== 12 ? 12 : 0) - (ampmB === 'AM' && hourB === 12 ? 12 : 0);
        
        return hour24A - hour24B;
      });
      
      // Group consecutive time slots (existing grouping logic)
      const ranges = [];
      let currentRange = { start: sortedTimeSlots[0], end: sortedTimeSlots[0] };
      
      for (let i = 1; i < sortedTimeSlots.length; i++) {
        const [currentTime, currentAmPm] = currentRange.end.split(' ');
        const currentHour = parseInt(currentTime);
        const hour24Current = currentHour + (currentAmPm === 'PM' && currentHour !== 12 ? 12 : 0) - (currentAmPm === 'AM' && currentHour === 12 ? 12 : 0);
        
        const [nextTime, nextAmPm] = sortedTimeSlots[i].split(' ');
        const nextHour = parseInt(nextTime);
        const hour24Next = nextHour + (nextAmPm === 'PM' && nextHour !== 12 ? 12 : 0) - (nextAmPm === 'AM' && nextHour === 12 ? 12 : 0);
        
        if (hour24Next === hour24Current + 1) {
          currentRange.end = sortedTimeSlots[i];
        } else {
          ranges.push(currentRange);
          currentRange = { start: sortedTimeSlots[i], end: sortedTimeSlots[i] };
        }
      }
      ranges.push(currentRange);
      
      // Format ranges with properly formatted close time
      return ranges.map(range => {
        const [endTime, endAmPm] = range.end.split(' ');
        const endHour = parseInt(endTime);
        const hour24End = endHour + (endAmPm === 'PM' && endHour !== 12 ? 12 : 0) - (endAmPm === 'AM' && endHour === 12 ? 12 : 0);
        
        // Parse closeTime and format it properly
        if (hour24End + 1 >= parseTime(closeTime)) {
          const formattedCloseTime = convertMilitaryTimeTo12HourFormat(closeTime);
          return `during ${range.start}-${formattedCloseTime}`;
        }
        
        // Calculate next hour normally
        let nextHour = endHour + 1;
        let nextAmPm = endAmPm;
        
        if (nextHour === 12) {
          nextAmPm = endAmPm === 'AM' ? 'PM' : 'AM';
        } else if (nextHour > 12) {
          nextHour = nextHour - 12;
        }
        
        return `during ${range.start}-${nextHour} ${nextAmPm}`;
      }).join(', ');
    };
    
    return {
      underutilized: {
        count: underutilizedHours,
        timeRanges: formatTimeRanges(underutilizedTimeSlots)
      },
      overutilized: {
        count: overutilizedHours,
        timeRanges: formatTimeRanges(overutilizedTimeSlots)
      }
    };
  };

  // Get utilization analysis
  const utilization = analyzeUtilization();

  // Enhance the description with utilization details
  let enhancedDescription = ""

  // Add underutilization details if significant
  if (utilization.underutilized.count >= 3) {
    enhancedDescription += `\nNursing capacity underutilized for ${utilization.underutilized.count} hours`;
    if (utilization.underutilized.timeRanges) {
      enhancedDescription += ` during: ${utilization.underutilized.timeRanges}`;
    }
  }

  // Add overutilization details if significant
  if (utilization.overutilized.count >= 2) {
    enhancedDescription += `\nNurses overloaded for ${utilization.overutilized.count} hours`;
    if (utilization.overutilized.timeRanges) {
      enhancedDescription += ` during: ${utilization.overutilized.timeRanges}`;
    }
  }

  return {
    title: "Nurse Workload Distribution",
    description: enhancedDescription,
    badge: statusInfo.badge,
    status: statusInfo.status,
    chartType: "stacked-bar",
    chartData: hourlyData,
    chartColors: colors,
    chartHeight,
    height,
    series: [
      { name: "Available", key: "available" },
      { name: "Near Capacity", key: "nearCapacity" },
      { name: "At Capacity", key: "atCapacity" },
      { name: "Over Capacity", key: "overCapacity" }
    ],
    xAxisKey: "timeSlot",
    xAxisLabel: "Hour of Day",
    yAxisLabel: "Average Number of Nurses",
    stats: calculateStats(),
    customFooter: <WorkloadTypeDropdown />,
    showLegend: true,
    legend: type === "acuity" ? [
      { label: "Available (<3 acuity)", color: colors[0] },
      { label: "Within Bandwidth (3-5 acuity)", color: colors[1] },
      { label: "Near Full Assignment (6-8 acuity)", color: colors[2] },
      { label: "Over Capacity (>9 acuity)", color: colors[3] }
    ] : [
      { label: "Available (0 or 1 patients)", color: colors[0] },
      { label: "Within Bandwidth (2 patients)", color: colors[1] },
      { label: "Near Full Assignment (3 patients)", color: colors[2] },
      { label: "Over Capacity (4+ patients)", color: colors[3] }
    ]
  }
}

export default createNurseWorkload 